import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { Modal } from "../components/ui";
import RichEditor from "../components/RichEditor";

export default function Approvals() {
  const [list, setList] = useState([]);
  const [requireApproval, setRequireApproval] = useState(true);
  const [flows, setFlows] = useState({});
  const [flowLabels, setFlowLabels] = useState({});
  const setFlow = async (fam, val) => {
    const next = { ...flows, [fam]: val === "" ? null : val === "on" };
    setFlows(next);
    await api.put("/approvals/flow-settings", { flow_overrides: next }).catch(() => {});
  };
  const [preview, setPreview] = useState(null);
  const [edit, setEdit] = useState(null); // {id, channel, subject, body_text, body_html}
  const [saving, setSaving] = useState(false);
  const openEdit = (m) => {
    setPreview(null);
    setEdit({ id: m.id, channel: m.channel, subject: m.subject || "", body_text: m.body_text || "", body_html: m.body_html || "" });
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = edit.channel === "sms"
        ? { body_text: edit.body_text }
        : { subject: edit.subject, ...(edit.body_html ? { body_html: edit.body_html } : { body_text: edit.body_text }) };
      await api.put(`/approvals/${edit.id}`, payload);
      setEdit(null);
      load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
    setSaving(false);
  };
  const [busy, setBusy] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/approvals?status=pending").then((r) => setList(r.data)).catch(() => {});
    api.get("/approvals/settings").then((r) => { setRequireApproval(r.data.require_approval); setFlows(r.data.flow_overrides || {}); setFlowLabels(r.data.flow_labels || {}); }).catch(() => {});
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const approve = async (id) => {
    setBusy(id);
    try { await api.post(`/approvals/${id}/approve`); setPreview(null); load(); }
    catch (e) { alert("Invio fallito: " + (e?.response?.data?.detail || e.message)); }
    setBusy(null);
  };
  const reject = async (id) => {
    if (!window.confirm("Rifiutare questa email? Non verrà inviata.")) return;
    setBusy(id);
    try { await api.post(`/approvals/${id}/reject`); setPreview(null); load(); }
    catch (e) { alert("Errore: " + e.message); }
    setBusy(null);
  };
  const toggle = async (val) => {
    setRequireApproval(val);
    await api.put("/approvals/settings", { require_approval: val }).catch(() => {});
  };

  const toggleSel = (id) => setSel((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = list.length > 0 && list.every((m) => sel.has(m.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(list.map((m) => m.id)));

  const bulkApprove = async () => {
    const ids = list.filter((m) => sel.has(m.id)).map((m) => m.id);
    if (!ids.length) return;
    if (!window.confirm(`Approvare e inviare ${ids.length} messaggi${ids.length === 1 ? "o" : ""}?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/approvals/bulk-approve", { ids });
      const { sent, failed } = r.data;
      let msg = `✅ Inviati: ${sent}`;
      if (failed?.length) msg += `\n⚠️ Falliti: ${failed.length}\n` + failed.slice(0, 8).map((f) => `• ${f.to}: ${f.error}`).join("\n");
      alert(msg);
      setSel(new Set());
      load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
    setBulkBusy(false);
  };

  return (
    <div className="page" data-testid="approvals-page">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Email e SMS da approvare {list.length > 0 && <span className="nav-badge">{list.length}</span>}</h2>
        <div className="spacer" />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={requireApproval} onChange={(e) => toggle(e.target.checked)} data-testid="approval-toggle" />
          Richiedi la mia approvazione prima di ogni invio automatico
        </label>
        <div className="toolbar" style={{ gap: 14, flexWrap: "wrap", marginTop: 8 }} data-testid="approval-flow-settings">
          {Object.entries(flowLabels).map(([fam, label]) => (
            <label key={fam} style={{ fontSize: 13 }}>{label}{" "}
              <select value={flows[fam] === true ? "on" : flows[fam] === false ? "off" : ""}
                onChange={(e) => setFlow(fam, e.target.value)} data-testid={`approval-flow-${fam}`}>
                <option value="">come impostazione globale</option>
                <option value="on">sempre in coda</option>
                <option value="off">invio diretto (senza coda)</option>
              </select>
            </label>
          ))}
        </div>
      </div>

      {!requireApproval && (
        <div className="panel" style={{ borderColor: "#b45309", background: "rgba(180,83,9,.08)" }}>
          ⚠️ L'approvazione è <strong>disattivata</strong>: le email automatiche verranno inviate ai clienti senza il tuo OK.
        </div>
      )}

      <div className="panel">
        {list.length === 0 ? (
          <div style={{ color: "#cbd5e1", fontSize: 14, padding: "6px 2px" }} data-testid="approvals-empty">✓ Nessuna email in attesa di approvazione.</div>
        ) : (
          <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <button className="btn sm" onClick={toggleAll} data-testid="approvals-select-all">{allSelected ? "Deseleziona tutto" : "Seleziona tutto"}</button>
            <button className="btn sm" data-testid="approvals-dedup" onClick={async () => {
              if (!window.confirm("Rimuovere i doppioni dalla coda? Per ogni email uguale (destinatario, oggetto, tipo) resta solo la più vecchia.")) return;
              const r = await api.post("/approvals/dedup");
              window.alert(`Doppioni rimossi: ${r.data.rimossi}. Email rimaste in coda: ${r.data.rimasti}.`);
              load();
            }}>🧹 Rimuovi doppioni</button>
            <button className="btn sm primary" onClick={bulkApprove} disabled={bulkBusy || sel.size === 0} data-testid="approvals-bulk-approve">
              {bulkBusy ? "Invio in corso…" : `✅ Approva e invia selezionati (${sel.size})`}
            </button>
          </div>
          <table>
            <thead><tr>
              <th style={{ width: 34 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Seleziona tutto" data-testid="approvals-check-all" /></th>
              <th>Destinatario</th><th>Oggetto</th><th>Tipo</th><th>Creata</th><th></th></tr></thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} data-testid={`approval-row-${m.id}`}>
                  <td><input type="checkbox" checked={sel.has(m.id)} onChange={() => toggleSel(m.id)} data-testid={`approval-check-${m.id}`} /></td>
                  <td>{m.channel === "sms" ? "📲 " : "✉️ "}{m.to}</td>
                  <td>{m.subject}</td>
                  <td className="muted">{m.label || m.kind}</td>
                  <td className="muted">{m.created_at ? new Date(m.created_at).toLocaleString("it-IT") : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn sm" onClick={() => setPreview(m)} data-testid={`approval-preview-${m.id}`}>Anteprima</button>{" "}
                    <button className="btn sm" onClick={() => openEdit(m)} data-testid={`approval-edit-${m.id}`}>✏️ Modifica</button>{" "}
                    <button className="btn sm primary" onClick={() => approve(m.id)} disabled={busy === m.id} data-testid={`approval-approve-${m.id}`}>{busy === m.id ? "Invio…" : "Approva e invia"}</button>{" "}
                    <button className="btn sm" onClick={() => reject(m.id)} disabled={busy === m.id} data-testid={`approval-reject-${m.id}`}>Rifiuta</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {preview && (
        <Modal title={`Anteprima · ${preview.subject}`} onClose={() => setPreview(null)} wide>
          <div className="muted" style={{ marginBottom: 10 }}>A: {preview.to}</div>
          <iframe title="preview" sandbox="allow-same-origin" srcDoc={(preview.body_html || `<pre>${preview.body_text || ""}</pre>`).replace(/src="cid:(news\d+)"/g, 'src="/api/drip/asset/$1.png"')}
            style={{ width: "100%", minHeight: 460, background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }} data-testid="approval-preview-frame" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn" onClick={() => openEdit(preview)} data-testid="approval-modal-edit">✏️ Modifica</button>
            <button className="btn" onClick={() => reject(preview.id)} disabled={busy === preview.id} data-testid="approval-modal-reject">Rifiuta</button>
            <button className="btn primary" onClick={() => approve(preview.id)} disabled={busy === preview.id} data-testid="approval-modal-approve">{busy === preview.id ? "Invio…" : "Approva e invia"}</button>
          </div>
        </Modal>
      )}
      {edit && (
        <Modal title={edit.channel === "sms" ? "✏️ Modifica SMS" : "✏️ Modifica email"} onClose={() => setEdit(null)} wide={edit.channel !== "sms"}>
          {edit.channel === "sms" ? (
            <>
              <div className="field"><label>Testo dell'SMS</label>
                <textarea rows={5} value={edit.body_text} onChange={(e) => setEdit({ ...edit, body_text: e.target.value })} data-testid="approval-edit-sms-text" />
              </div>
              <p className="muted" style={{ fontSize: 12 }}>{edit.body_text.length} caratteri{edit.body_text.length > 160 ? ` · ${Math.ceil(edit.body_text.length / 153)} SMS` : ""}</p>
            </>
          ) : (
            <>
              <div className="field"><label>Oggetto</label>
                <input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} data-testid="approval-edit-subject" />
              </div>
              {edit.body_html ? (
                <RichEditor html={edit.body_html} onChange={(h) => setEdit((s) => ({ ...s, body_html: h }))} testid="approval-edit-body" minHeight={280} />
              ) : (
                <div className="field"><label>Testo</label>
                  <textarea rows={10} value={edit.body_text} onChange={(e) => setEdit({ ...edit, body_text: e.target.value })} data-testid="approval-edit-text" />
                </div>
              )}
              <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>La versione modificata verrà inviata così com'è (le lezioni del funnel non verranno rigenerate).</p>
            </>
          )}
          <div className="modal-actions">
            <button className="btn" onClick={() => setEdit(null)}>Annulla</button>
            <button className="btn primary" onClick={saveEdit} disabled={saving} data-testid="approval-edit-save">{saving ? "Salvo…" : "Salva modifiche"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
